"use client";
import InlineBanner from "@/app/components/banners/inlineBanner";
import { useLocationDetails } from "@/app/hooks/useLocationDetails";
import { useViewMode, ViewMode } from "@/app/hooks/useViewMode";
import ProductCard from "@/app/search/products/productCard";
import SortByPanel from "@/components/sort-by-panel/sortByPanel";
import { SearchTypes } from "@/declarations/search";
import { SearchResults } from "@/fetch/algolia/getSearchResults";
import { useAppDispatch } from "@/store/hooks";
import { getBestPrices } from "@/store/searchSlice";
import { setSearchViewMode } from "@/store/searchViewModeSlice";
import { AppState } from "@/store/store";
import { fetchBanner, TopsortBannerConfig, WinnerResult } from "@/utils/topsortUtil";
import { useWindowWidth } from "@react-hook/window-size";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Fragment } from "react/jsx-runtime";
import style from "./baseSearchPage.module.scss";

export type DeviceType = "mobile" | "tablet" | "desktop";

export type BaseSearchPageResultsProps = {
  pageURI: string;
  searchResults: SearchResults;
  basePosition: number;
  shippingRegionId: number;
  topsortBannerConfig?: TopsortBannerConfig;
  indexName: string;
  userToken: string | undefined;
  viewMode: ViewMode;
};
export default function BaseSearchPageResults({
  pageURI,
  searchResults,
  basePosition,
  shippingRegionId,
  topsortBannerConfig,
  indexName,
  userToken,
  viewMode: initialViewMode,
}: BaseSearchPageResultsProps) {
  const reduxViewMode = useSelector((state: AppState) => state.searchViewModeSlice.viewMode);
  const topsortJsApiKey = useSelector((state: AppState) => state.config.topsortJsApiKey);

  // Use custom hook for view mode (reads from cookies first, then state)
  let { viewMode, updateViewMode } = useViewMode(initialViewMode);

  //if redux viewMode is available, use it
  if (reduxViewMode !== "") {
    viewMode = reduxViewMode as ViewMode;
  }

  // Handle view mode changes
  const handleViewModeChange = useCallback(
    (newViewMode: ViewMode) => {
      updateViewMode(newViewMode);
      dispatch(setSearchViewMode(newViewMode));
    },
    [updateViewMode],
  );

  const windowWidth = useWindowWidth();

  const [deviceType, setDeviceType] = useState<DeviceType>("desktop");

  const dispatch = useAppDispatch();

  const { zipCode } = useLocationDetails();

  const topsortBannerType = useMemo(() => {
    return windowWidth < 576 ? "mobile" : "desktop";
  }, [windowWidth]);

  const [topsortWinnerType, setTopsortWinnerType] = useState<WinnerResult | null>(null);

  const topsortHitsHash = useMemo(() => {
    if (!topsortBannerConfig || !searchResults.result.hits || searchResults.result.hits.length === 0) {
      return null;
    }
    const hits = searchResults.result.hits as SearchTypes.ProductHit[];
    return hits.map((hit) => hit.mp_id).join("|");
  }, [topsortBannerConfig, searchResults.result.hits]);

  function chunkArray<T>(arr: T[], size: number): T[][] {
    return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));
  }
  useEffect(() => {
    if ((searchResults.result.hits.length > 0 || searchResults.sponsoredListings.length > 0) && zipCode) {
      // Get mpIds from regular search results
      const regularMpIds = searchResults.result.hits.map((hit): number => hit.mp_id);

      // Get mpIds from sponsored products
      const sponsoredMpIds = searchResults.sponsoredListings.map((hit): number => hit.mp_id);

      // Combine mpIds, avoiding duplicates using Set
      const mpIdsSet = new Set([...regularMpIds, ...sponsoredMpIds]);
      const mpIds = Array.from(mpIdsSet);

      const chunks = chunkArray(mpIds, 12); //12 is the number of products/cards that appear in a high resolution screen
      chunks.forEach((chunk) => {
        dispatch(
          getBestPrices({
            mpIds: chunk,
            postalCode: zipCode,
          }),
        );
      });
    }
  }, [dispatch, searchResults.result.hits, searchResults.sponsoredListings, pageURI, zipCode]);

  useEffect(() => {
    let newDeviceType: DeviceType;
    if (windowWidth < 576) newDeviceType = "mobile";
    else if (windowWidth < 768) newDeviceType = "tablet";
    else newDeviceType = "desktop";
    if (newDeviceType != deviceType) setDeviceType(newDeviceType);
  }, [windowWidth, deviceType]);

  useEffect(() => {
    setTopsortWinnerType(null);
  }, [topsortBannerType]);

  const lastFetchedHashRef = useRef<string | null>(null);
  const lastBannerTypeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!topsortBannerConfig || !topsortHitsHash) return;

    const isNewHash = topsortHitsHash !== lastFetchedHashRef.current;
    const bannerTypeChanged = topsortBannerType !== lastBannerTypeRef.current;

    if (isNewHash || bannerTypeChanged) {
      lastFetchedHashRef.current = topsortHitsHash;
      lastBannerTypeRef.current = topsortBannerType;

      fetchBanner(
        topsortBannerConfig.auctionType,
        topsortBannerConfig.inline,
        topsortJsApiKey,
        topsortBannerType,
        topsortBannerConfig.filter,
      )
        .then((winnerData) => {
          setTopsortWinnerType(winnerData);
        })
        .catch(console.error)
        .finally(() => {});
    }
  }, [topsortBannerConfig, topsortHitsHash, topsortBannerType, topsortJsApiKey]);

  return (
    <div className={style.searchResultsWrapper}>
      <section id="sortBar" className={`${style.customCard} ${style.desktopSort}`}>
        <SortByPanel pageURI={pageURI} viewModeChangeCallback={handleViewModeChange} viewMode={viewMode} />
      </section>

      <div className={`search-results ${style.results}` + (viewMode === "list" ? ` ${style.listView}` : ``)}>
        {searchResults.sponsoredListings.map((hit, index) => {
          return (
            <Fragment key={hit.mp_id}>
              <ProductCard
                {...hit}
                queryId={searchResults.result.queryID}
                position={basePosition + index + 1}
                viewMode={viewMode === "list" ? "list" : "grid"}
                shippingRegionId={shippingRegionId}
                deviceType={deviceType}
                indexName={indexName}
                userToken={userToken}
              />
            </Fragment>
          );
        })}

        {searchResults.result.hits.map((hit, index) => {
          return (
            <Fragment key={hit.mp_id}>
              {searchResults.sponsoredListings.length + index === 6 && topsortWinnerType?.found && (
                <InlineBanner topsortWinner={topsortWinnerType.winner} topsortBannerType={topsortBannerType} />
              )}

              <ProductCard
                {...hit}
                queryId={searchResults.result.queryID}
                position={basePosition + index + 1}
                viewMode={viewMode === "list" ? "list" : "grid"}
                shippingRegionId={shippingRegionId}
                deviceType={deviceType}
                indexName={indexName}
                userToken={userToken}
              />
            </Fragment>
          );
        })}

        {searchResults.sponsoredListings.length + searchResults.result.hits.length < 7 && topsortWinnerType?.found && (
          <InlineBanner topsortWinner={topsortWinnerType.winner} topsortBannerType={topsortBannerType} />
        )}
      </div>
    </div>
  );
}
